import React, { useState } from 'react';
import { Menu, User, Flower, Moon, Sun, Languages, ArrowLeft, Clock, Bell, Search } from 'lucide-react';
import { UserProfile, BrandingConfig, Tab } from '../types';
import { useLanguage } from './LanguageContext';
import SyncStatusIndicator from './SyncStatusIndicator';
import { GlobalSearch } from './GlobalSearch';
import { PWAInstallButton } from './PWAInstallButton';

export const Header: React.FC<{ 
  toggleSidebar: () => void;
  user: UserProfile | null;
  onOpenProfile: () => void;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  onOpenReminder: () => void;
  onOpenSadhanaReminder?: () => void;
  branding: BrandingConfig;
  language: string;
  onToggleLanguage: () => void;
  onBack?: () => void;
  showBack?: boolean;
  onGoHome?: () => void;
  onNavigate?: (tab: Tab, data?: any) => void;
  onOpenEkadashi?: () => void;
  onOpenBadges?: () => void;
  onOpenCommunity?: () => void;
}> = ({ 
  toggleSidebar, 
  user, 
  onOpenProfile, 
  isDarkMode, 
  onToggleDarkMode, 
  onOpenReminder, 
  onOpenSadhanaReminder,
  branding, 
  language, 
  onToggleLanguage, 
  onBack, 
  showBack,
  onGoHome,
  onNavigate,
  onOpenEkadashi,
  onOpenBadges,
  onOpenCommunity
}) => {
  const logoHeight = Math.min(branding.logoHeight || 80, 48);
  const { t } = useLanguage();
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  return (
    <div className="sticky top-0 z-50 flex flex-col w-full shadow-md">
      <header className="bg-[#FDF5E6] text-templeRed border-b border-saffron/30 dark:bg-stone-800 dark:text-cream dark:border-stone-700 transition-colors">
        <div className="max-w-7xl mx-auto px-2 sm:px-4">
          <div className="flex justify-between items-center h-14 py-1 overflow-visible">
            
            {/* Left: Branding & Navigation */}
            <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
              {showBack && onBack && (
                <button 
                  onClick={onBack}
                  className="flex-shrink-0 p-1.5 rounded-md hover:bg-templeRed/10 dark:hover:bg-stone-700 transition-colors text-templeRed dark:text-saffron"
                  aria-label="Go Back"
                >
                  <ArrowLeft size={20} />
                </button>
              )}
              
              <button 
                onClick={toggleSidebar} 
                className="flex-shrink-0 p-1.5 rounded-md hover:bg-templeRed/10 dark:hover:bg-stone-700 transition-colors"
                aria-label="Toggle Navigation"
                data-testid="menu-button"
              >
                <Menu size={22} />
              </button>
              <button 
                onClick={onGoHome}
                className="flex items-center cursor-pointer hover:opacity-80 transition-opacity min-w-0"
                data-testid="logo-home-btn"
                title="Go to Home"
              >
                {branding.logoUrl ? (
                  <img 
                    src={branding.logoUrl} 
                    alt="Logo" 
                    className="object-contain transition-all max-w-[100px] sm:max-w-none" 
                    style={{ height: `${showBack ? Math.min(logoHeight, 36) : logoHeight}px`, width: 'auto', maxHeight: '48px' }}
                  />
                ) : (
                  <Flower 
                    className="text-saffron animate-pulse-slow" 
                    style={{ height: `${logoHeight}px`, width: `${logoHeight}px` }}
                  />
                )}
              </button>
            </div>

            {/* Right: Actions - Compact Icon Bar */}
            <div className="flex items-center gap-[3px] sm:gap-1 flex-shrink-0">
              
              {/* Global Search */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className={`flex-shrink-0 p-1 sm:p-1.5 rounded-lg transition-all active:scale-90 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center ${
                  isSearchOpen 
                    ? 'bg-saffron text-white shadow-md shadow-saffron/30' 
                    : 'text-saffron hover:bg-saffron/10'
                }`}
                title={language === 'hi' ? 'खोजें' : 'Search'}
                data-testid="global-search-btn"
              >
                <Search size={18} strokeWidth={2.5} />
              </button>

              {/* Language Switcher - Text only, no icon */}
              <button
                onClick={onToggleLanguage}
                className="flex-shrink-0 px-1.5 sm:px-2 py-0.5 rounded-lg text-[10px] sm:text-xs font-black text-saffron hover:bg-saffron/10 transition-all active:scale-90"
                title={t('header_lang')}
              >
                {language === 'en' ? 'हि' : 'EN'}
              </button>

              {/* Divider */}
              <div className="w-px h-4 bg-saffron/20 mx-0.5 hidden sm:block"></div>

              {/* Divine Reminder Alarm */}
              <button
                onClick={onOpenReminder}
                className="flex-shrink-0 p-1 sm:p-1.5 rounded-lg text-saffron hover:bg-saffron/10 transition-all active:scale-90 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center"
                title={t('header_reminder')}
              >
                <Clock size={18} strokeWidth={2} />
              </button>

              {/* Sadhana Reminder Bell */}
              {onOpenSadhanaReminder && (
                <button
                  onClick={onOpenSadhanaReminder}
                  className="flex-shrink-0 p-1 sm:p-1.5 rounded-lg text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all active:scale-90 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center"
                  title="साधना रिमाइंडर"
                  data-testid="header-sadhana-reminder"
                >
                  <Bell size={18} strokeWidth={2} />
                </button>
              )}

              {/* Cloud Sync Status - smaller */}
              {user && !user.isGuest && (
                <div 
                  className="w-6 h-6 sm:w-7 sm:h-7 flex items-center justify-center"
                  title="Sync Status"
                  data-testid="sync-status-indicator"
                >
                  <SyncStatusIndicator size="sm" />
                </div>
              )}

              {/* Dark Mode Toggle */}
              <button 
                onClick={onToggleDarkMode}
                className="flex-shrink-0 p-1 sm:p-1.5 rounded-lg text-saffron hover:bg-saffron/10 transition-all active:scale-90 w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center"
                title={isDarkMode ? t('header_theme_light') : t('header_theme_dark')}
              >
                {isDarkMode ? (
                  <Sun size={18} strokeWidth={2} />
                ) : (
                  <Moon size={18} strokeWidth={2} />
                )}
              </button>

              {/* PWA Install Button */}
              <PWAInstallButton variant="header" language={language} />

              {/* Profile Access - Compact */}
              {user && (
                <button 
                  onClick={onOpenProfile}
                  className="flex-shrink-0 flex items-center hover:bg-saffron/10 rounded-full transition-all active:scale-95 p-0.5"
                  title={t('profile')}
                  data-testid="header-profile-btn"
                >
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-saffron to-orange-500 overflow-hidden flex items-center justify-center border-2 border-white dark:border-stone-700 shadow-sm flex-shrink-0">
                    {user.avatar ? (
                      <img src={user.avatar} className="w-full h-full object-cover" alt="Profile" />
                    ) : (
                      <User size={14} className="text-white" />
                    )}
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </header>
      
      {/* Global Search Dropdown */}
      <GlobalSearch 
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onNavigate={(tab, data) => {
          if (onNavigate) onNavigate(tab, data);
          setIsSearchOpen(false);
        }}
        user={user}
        onOpenEkadashi={onOpenEkadashi}
        onOpenBadges={onOpenBadges}
        onOpenCommunity={onOpenCommunity}
        onOpenReminders={onOpenSadhanaReminder}
      />
    </div>
  );
};